import { Color } from '@persona-id/design-tokens';
import { CircleCheckIcon, CircleMinusIcon, CircleXIcon } from '@persona-id/design-tokens/icons';
import { upperFirst } from 'lodash/fp';
import startCase from 'lodash/fp/startCase';
import { DateTime } from 'luxon';
import styled from 'styled-components';

import { MULTIPLE_CHECKS_STATUS } from 'dashboard/components/ChecksTable/ChecksTableRowMultipleChecks';
import {
  CheckMetadata,
  CheckMetadataItem,
  CheckMetadataItemType,
  renderCheckNameCell,
  renderMatchResultCell,
  renderRequiredCell,
  renderStatusCell,
} from 'dashboard/components/ChecksTable/computeCheckMetadata';
import Permissioned from 'dashboard/components/Permissioned';
import Pii from 'dashboard/components/Pii';
import { TextWithDashedUnderline } from 'dashboard/components/Typography';
import HelpIcon from 'dashboard/components-lib/HelpIcon/HelpIcon';
import { formatDate } from 'dashboard/lib/utils/formatters';
import {
  AamvaIdentityComparison,
  Check,
  CheckAttributeMatch,
  CheckName,
  CheckStatus,
  MatchRequirement,
} from 'dashboard/models/check';
import { VerificationAamva } from 'dashboard/models/verification';
import Sticker from 'design-system/components/Sticker';
import Tooltip from 'design-system/components/Tooltip';
import { CHECK_DISPLAY_CONFIG } from 'lib/constants/check';
import { Permission } from 'lib/permissions';
import { sortChecks } from 'lib/utils/checks';

const NameMatchCard = styled.div`
  min-width: 150px;
`;

const ATTRIBUTE_CHECK_NAMES = [
  CheckName.AamvaAddressComparison,
  CheckName.AamvaBirthdateComparison,
  CheckName.AamvaIdentificationNumberComparison,
  CheckName.AamvaNameComparison,
  CheckName.AamvaIssueDateComparison,
  CheckName.AamvaExpirationDateComparison,
];

enum IdentityComparisonAttribute {
  IdentificationNumber = 'identification_number',
  Name = 'name',
  Birthdate = 'birthdate',
  PostalCode = 'postal_code',
  IssueDate = 'issue_date',
  ExpirationDate = 'expiration_date',
}

const IDENTITY_COMPARISON_ATTRIBUTES = [
  IdentityComparisonAttribute.IdentificationNumber,
  IdentityComparisonAttribute.Name,
  IdentityComparisonAttribute.Birthdate,
  IdentityComparisonAttribute.PostalCode,
  IdentityComparisonAttribute.IssueDate,
  IdentityComparisonAttribute.ExpirationDate,
];

export const computeAamvaAttributeChecksMetadata = (
  verification: VerificationAamva,
): CheckMetadata => {
  const sortedChecks = sortChecks(verification.attributes?.checks || []);
  const isRedacted = verification.attributes?.['redacted-at'] != null;

  const identityComparisonCheck = sortedChecks.find(
    (check) => check.name === CheckName.AamvaIdentityComparison,
  );

  // Note: There are two distinct tables that can be rendered for AAMVA verifications, because
  // many customers do not have the AamvaIdentityComparison check enabled and required.
  const displayIdentityComparisonCheckTable =
    identityComparisonCheck && identityComparisonCheck.requirement === MatchRequirement.Required;

  if (displayIdentityComparisonCheckTable) {
    return computeMetadataForIdentityComparisonCheck(
      identityComparisonCheck as AamvaIdentityComparison,
      verification,
      isRedacted,
    );
  }

  return computeMetadataForAttributeChecks(sortedChecks, verification, isRedacted);
};

function computeMetadataForIdentityComparisonCheck(
  identityComparisonCheck: AamvaIdentityComparison,
  verification: VerificationAamva,
  isRedacted: boolean,
) {
  const rows = IDENTITY_COMPARISON_ATTRIBUTES.map((comparisonAttribute) => {
    const failedComparisons = identityComparisonCheck.metadata?.['failed-comparisons'] ?? [];
    const failedItem = failedComparisons.find(
      (item) => item['aamva-field'] === comparisonAttribute,
    );

    let riskLevel;
    if (failedItem) {
      const riskLevelRaw = failedItem['risk-level'];
      riskLevel = startCase(riskLevelRaw.replace('_', ' '));
    } else {
      riskLevel = 'None';
    }

    return [
      startCase(comparisonAttribute.replace('_', ' ')),
      renderInputCell(verification, comparisonAttribute, isRedacted),
      riskLevel,
    ];
  }).filter(Boolean);

  return {
    metadata: [
      {
        type: CheckMetadataItemType.Table,
        heading: {
          title: 'Identity Comparison Check',
          sticker: renderRiskLevelSticker(identityComparisonCheck),
        },
        rowHeaders: [
          'Property',
          'Input',
          <>
            Risk Level
            <HelpIcon>
              Some issuing authorities cannot verify certain properties, so accurate submitted info
              can still result in a reported mismatch, but is low risk. If we are confident the
              authority consistently stores this data, a mismatch is high risk.
            </HelpIcon>
          </>,
        ],
        rows,
      },
    ] as CheckMetadataItem[],
  };
}

function renderRiskLevelSticker(identityComparisonCheck: AamvaIdentityComparison) {
  if (
    identityComparisonCheck.status === 'passed' &&
    identityComparisonCheck.reasons &&
    identityComparisonCheck.reasons.includes('medium_risk')
  ) {
    return <Sticker variant='yellow'>Medium Risk</Sticker>;
  }

  switch (identityComparisonCheck.status) {
    case CheckStatus.Passed:
      return <Sticker variant='success'>Low Risk</Sticker>;
    case CheckStatus.Failed:
      return <Sticker variant='danger'>High Risk</Sticker>;
    case CheckStatus.NotApplicable:
      return <Sticker variant='yellow'>N/A</Sticker>;
    default:
      return null;
  }
}

function renderAamvaMatchResultCell(check: Check) {
  // Only render the special match result for the name comparison check - as it has special metadata
  if (check?.name === CheckName.AamvaNameComparison) {
    return (
      <Tooltip
        side='right'
        trigger={
          <TextWithDashedUnderline>
            {/** @ts-expect-error TODO: AamvaNameComparison is not typed properly, please fix */}
            {upperFirst(check?.metadata?.['match-result'])}
          </TextWithDashedUnderline>
        }
      >
        <NameMatchCard>
          <strong>First Name Match:</strong>{' '}
          {/** @ts-expect-error TODO: AamvaNameComparison is not typed properly, please fix */}
          {getAamvaMetadataIcon(check.metadata?.['name-first-match'] as CheckAttributeMatch)}
          <br />
          <strong>Last Name Match:</strong>{' '}
          {/** @ts-expect-error TODO: AamvaNameComparison is not typed properly, please fix */}
          {getAamvaMetadataIcon(check.metadata?.['name-last-match'] as CheckAttributeMatch)}
        </NameMatchCard>
      </Tooltip>
    );
  }

  /** @ts-expect-error TODO: AamvaNameComparison is not typed properly, please fix */
  return renderMatchResultCell(check?.metadata?.['match-result']);
}

function computeMetadataForAttributeChecks(
  sortedChecks: Check[],
  verification: VerificationAamva,
  isRedacted: boolean,
) {
  const rows = sortedChecks
    .map((check) => {
      if (!ATTRIBUTE_CHECK_NAMES.includes(check.name)) {
        return null;
      }

      const checkDetails = CHECK_DISPLAY_CONFIG[check.name];
      if (checkDetails == null) {
        return null;
      }

      return [
        renderCheckNameCell(checkDetails.name, checkDetails.description),
        renderInputCell(verification, check.name, isRedacted),
        renderAamvaMatchResultCell(check),
        renderStatusCell(check.status),
        renderRequiredCell(check.requirement === MatchRequirement.Required),
      ];
    })
    .filter(Boolean);

  return {
    metadata: [
      {
        type: CheckMetadataItemType.Table,
        heading: {
          title: 'Attribute Checks',
        },
        rowHeaders: ['Property', 'Input', 'Match Result', 'Status', 'Required'],
        rows,
      },
    ] as CheckMetadataItem[],
  };
}

function renderInputCell(
  verification: VerificationAamva,
  attributeIdentifier: string,
  isRedacted: boolean,
) {
  switch (attributeIdentifier) {
    case CheckName.AamvaAddressComparison:
    case IdentityComparisonAttribute.PostalCode:
      return <Pii isRedacted={isRedacted}>{verification.attributes['address-postal-code']}</Pii>;

    case CheckName.AamvaNameComparison:
    case IdentityComparisonAttribute.Name: {
      const nameFull = [verification.attributes['name-first'], verification.attributes['name-last']]
        .filter(Boolean)
        .join(' ');
      return <Pii isRedacted={isRedacted}>{nameFull}</Pii>;
    }

    case CheckName.AamvaBirthdateComparison:
    case IdentityComparisonAttribute.Birthdate: {
      const birthdate = verification.attributes.birthdate;
      const birthdateFormatted = birthdate
        ? `${formatDate(birthdate)} (
          ${Math.floor(DateTime.local().diff(DateTime.fromISO(birthdate), 'years').years)} years
          old)`
        : null;
      return <Pii isRedacted={isRedacted}>{birthdateFormatted}</Pii>;
    }

    case CheckName.AamvaIdentificationNumberComparison:
    case IdentityComparisonAttribute.IdentificationNumber:
      return (
        <Permissioned
          permission={Permission.VerificationGovIdNumberView}
          fallback={
            <Sticker variant='basic' data-test='checks-table-aamva__id__not-applicable'>
              N/A
            </Sticker>
          }
        >
          <Pii isRedacted={isRedacted}>{verification.attributes['identification-number']}</Pii>
        </Permissioned>
      );

    case CheckName.AamvaIssueDateComparison:
    case IdentityComparisonAttribute.IssueDate:
      return <Pii isRedacted={isRedacted}>{formatDate(verification.attributes['issue-date'])}</Pii>;

    case CheckName.AamvaExpirationDateComparison:
    case IdentityComparisonAttribute.ExpirationDate:
      return (
        <Pii isRedacted={isRedacted}>{formatDate(verification.attributes['expiration-date'])}</Pii>
      );

    default:
      return null;
  }
}

const iconProps = {
  height: 20,
  width: 20,
};

const getAamvaMetadataIcon = (result: CheckAttributeMatch) => {
  switch (result) {
    case CheckAttributeMatch.Full:
      return <CircleCheckIcon color={Color.BorderIconSuccess} {...iconProps} />;
    case CheckAttributeMatch.Partial:
      return <CircleMinusIcon color={Color.BorderIconWarning} {...iconProps} />;
    case CheckAttributeMatch.None:
      return <CircleXIcon color={Color.BorderIconDanger} {...iconProps} />;
    case CheckAttributeMatch.Missing:
      return <CircleMinusIcon color={Color.BorderIconWarning} {...iconProps} />;
    default:
      return <CircleMinusIcon color={Color.BorderIconWarning} {...iconProps} />;
  }
};

export function getStatusForCheckRow(verification: VerificationAamva) {
  const sortedChecks = sortChecks(verification.attributes?.checks || []);

  const identityComparisonCheck = sortedChecks.find(
    (check) => check.name === CheckName.AamvaIdentityComparison,
  );

  if (
    identityComparisonCheck &&
    identityComparisonCheck.requirement === MatchRequirement.Required
  ) {
    return identityComparisonCheck.status;
  }

  return MULTIPLE_CHECKS_STATUS;
}
